import React from 'react';

import { Box, Button, Divider, Typography } from '@mui/material';
import { isNotNil, upperFirst } from 'es-toolkit';

import { DEFAULT_VALUE_IF_NIL } from '@clearverify/constants';

import {
  useVerificationRequestPaymentInfoQuery,
  VerifierCredentialStatus,
  VerifierType,
} from 'src/api';
import { VERIFIED_STATUS_ICON } from 'src/assets';
import { Tooltip } from 'src/components/common';
import { DemoPrivacyAvatar, DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { InfoBlock } from 'src/components/shared';
import { reportAvailableVerificationRequestStatuses } from 'src/constants/inbox';
import { ModalId } from 'src/constants/modals';
import { VERIFICATION_REQUEST_TYPE_LABEL } from 'src/constants/verificationRequest';
import { useGeneratedReports } from 'src/hooks/generatedReport';
import { useInboxItemParamId } from 'src/hooks/inboxItem';
import { useModal } from 'src/hooks/shared';
import {
  useVerificationRequest,
  useVerificationRequestPaymentInfoSubscription,
} from 'src/hooks/verificationRequest';
import { useVerifierByVerificationRequestId } from 'src/hooks/verifier';
import { ViewVerificationRequestReportModalArgs } from 'src/modals/verifier';
import type { SxProp } from 'src/types/mui';
import { composeSx } from 'src/utils/styles';

import { requestBodyContainerCVStyles, viewReportButtonStyles } from './shared/styles';

export const InboxItemContentVerificationRequestCVStaff: React.FC = () => {
  const { openModal } = useModal();

  const inboxItemId = useInboxItemParamId();

  const { verificationRequest, loading: verificationRequestLoading } = useVerificationRequest({
    inboxItemId,
  });
  const { data: verificationRequestPaymentInfo } = useVerificationRequestPaymentInfoQuery({
    variables: {
      where: { inboxItemId },
    },
    skip: !inboxItemId,
  });
  const { verifier, loading: verifierLoading } = useVerifierByVerificationRequestId(
    verificationRequest?.id,
  );
  const { generatedReportsMeta, loading } = useGeneratedReports({ inboxItemId });

  const reportIsGenerated = React.useMemo(
    () => generatedReportsMeta.some(isNotNil),
    [generatedReportsMeta],
  );

  const isReportAvailable = React.useMemo(
    () =>
      verificationRequest?.status &&
      reportAvailableVerificationRequestStatuses.includes(verificationRequest.status),
    [verificationRequest?.status],
  );

  const isGovernmentVerifier = React.useMemo(
    () => verifier?.type === VerifierType.Government,
    [verifier?.type],
  );

  const stripePaymentStatus = React.useMemo(
    () => verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus,
    [verificationRequestPaymentInfo?.verificationRequest?.stripePaymentStatus],
  );

  useVerificationRequestPaymentInfoSubscription({ inboxItemId, skip: isGovernmentVerifier });

  const handleViewReportClick = React.useCallback(() => {
    openModal(ModalId.ViewVerificationRequestReportModal, {
      verificationRequest,
      generatedReportsMeta,
      inboxItemId,
    } satisfies ViewVerificationRequestReportModalArgs);
  }, [generatedReportsMeta, inboxItemId, openModal, verificationRequest]);

  return (
    <Box sx={requestBodyContainerCVStyles}>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" gap={1}>
          <DemoPrivacyAvatar sx={avatarStyles} src={verifier?.logo?.downloadUrl} />
          <Box>
            <Typography variant="body1" fontWeight={600} lineHeight={1.3}>
              <DemoPrivacyText>{verifier?.displayName}</DemoPrivacyText>
            </Typography>

            <Typography
              variant="body1"
              lineHeight={1.3}
              sx={theme => ({
                color: theme.palette.text.secondary,
                mr: 0.5,
              })}
            >
              Verifier
            </Typography>
          </Box>
        </Box>

        {verifier?.credentialStatus === VerifierCredentialStatus.Credentialed && (
          <Box sx={{ fontSize: 0 }}>
            <Tooltip title={credentialedTooltipText} arrow placement="right">
              <img width="21" height={21} alt="verifiedStatusIcon" src={VERIFIED_STATUS_ICON} />
            </Tooltip>
          </Box>
        )}
      </Box>

      <Divider sx={requestBodyDividerStyles} />

      <InfoBlock.Container mt={2} gap={1.5}>
        <InfoBlock.Item
          title="Report Type"
          value={
            verificationRequest?.type
              ? VERIFICATION_REQUEST_TYPE_LABEL[verificationRequest.type]
              : DEFAULT_VALUE_IF_NIL
          }
          loading={verificationRequestLoading}
        />
        <InfoBlock.Item
          title="Contact Name"
          value={verifier?.contactName}
          loading={verifierLoading}
        />
        <InfoBlock.Item
          title="Contact Email"
          value={verifier?.contactEmail}
          loading={verifierLoading}
        />
        <InfoBlock.Item
          title="Contact Phone"
          value={verifier?.contactPhone}
          loading={verifierLoading}
        />

        {stripePaymentStatus && !isGovernmentVerifier && (
          <InfoBlock.Item
            title="Payment Status"
            value={upperFirst(stripePaymentStatus)}
            loading={verificationRequestLoading}
          />
        )}
      </InfoBlock.Container>

      {isReportAvailable && (
        <Box display="flex" justifyContent="flex-end" mt={4}>
          <Button
            variant="contained"
            disabled={!reportIsGenerated}
            loading={loading}
            sx={composeSx(viewReportButtonStyles, { width: '125px' })}
            onClick={handleViewReportClick}
          >
            View report
          </Button>
        </Box>
      )}
    </Box>
  );
};

const requestBodyDividerStyles: SxProp = theme => ({
  mt: 2.5,
  borderColor: theme.palette.secondary.main,
});

const avatarStyles: SxProp = { width: 42, height: 42 };

const credentialedTooltipText =
  'This verifier has been thoroughly vetted and certified by Clear Verify, confirming their identity, locations, standing with government agencies, and more.';
